
import Vue from 'vue'
import Component from 'vue-class-component'
import { State } from 'vuex-class'
import { Prop } from 'vue-property-decorator'

import TempAccountPrint from '@/components/TempAccounts/TempAccountPrint.vue'
import BlueButton from '../Atomic/BlueButton.vue'
import GreenButton from '../Atomic/GreenButton.vue'
import RedButton from '../Atomic/RedButton.vue'

import TempAccount from '@/models/tempAccount'
import Service from '@/models/service'

import TempAccountService from '@/services/TempAccountService'
import ServiceService from '@/services/ServiceService'
import ErrorHandler from '@/services/ErrorHandlerService'
import DatePicker from '@/components/Atomic/DatePicker.vue'
import NotificationService, {
  INotificationService,
} from '@/services/NotificationService'
import CardTitle from '@/components/Atomic/CardTitle.vue'
import ProgressLinear from '@/components/Atomic/ProgressLinear.vue'

const Props = Vue.extend({
  props: {
    onClose: Function,
  },
})

@Component({
  components: {
    TempAccountPrint,
    BlueButton,
    GreenButton,
    RedButton,
    DatePicker,
    CardTitle,
    ProgressLinear,
  },
})
export default class TempAccountCreate extends Props {
  @State('user') private currentUser!: any
  @Prop() private readonly descriptions!: string[]

  private dialog: boolean = false
  private isPending: boolean = false
  private stepper: number = 1

  private startDateMenu = false
  private expiryDateMenu = false

  private phoneNumberRegExp = new RegExp(/^[\d]{10,19}$/)
  private validPhoneNumber: boolean = false
  private validEmail: boolean = false
  private validatingPhoneNumber: boolean = false
  private validatingEmailAddress: boolean = false
  private lastCheckedPhoneNumber: string = ''
  private lastCheckedEmail: string = ''
  private accountExists: boolean = false
  private emailExists: boolean = false
  private existingAccount: TempAccount = {} as TempAccount

  private $notificationService!: INotificationService
  private errorHandler = new ErrorHandler()

  private tempAccount: TempAccount = {} as TempAccount
  private services: Service[] = []
  private enabledService: number[] = []
  private selectedSchool: string | null = null
  private schoolToSelect: boolean = false
  private maxDateAllowed: string = ''
  private title: string[] = ['Monsieur', 'Madame']

  public show() {
    this.initialize()
    this.dialog = true
    this.fetchServices()
  }

  private async fetchServices() {
    this.isPending = true
    try {
      this.services = await ServiceService.availableServices(
        this.currentUser.roles,
        'Basique',
      )
    } catch (error: any) {
      if (error.response !== undefined) {
        this.errorHandler.handle(this, error.response.data.code, true)
      }
    } finally {
      this.isPending = false
    }
  }

  private hide() {
    this.validPhoneNumber = false
    this.validEmail = false
    this.lastCheckedEmail = ''
    this.onClose()
    this.dialog = false
    this.enabledService = []
    this.selectedSchool = null
  }

  private formatDate(date: Date | string | undefined) {
    return date === undefined ? '' : (this as any).$moment(date).format('L')
  }

  private initialize() {
    this.stepper = 1

    this.tempAccount = {} as TempAccount
    this.tempAccount.date_validite = (this as any)
      .$moment(new Date())
      .format('YYYY-MM-DD')
    this.$validator.reset()
  }

  private async next() {
    const validated = await this.$validator.validateAll()
    if (validated) {
      this.stepper++
      if (this.stepper === 2) {
        const wifi = this.services.find((service) => service.ad_group_name === 'radius')
        if (wifi !== undefined) {
          this.updateEnabledServices(wifi.groupe_ad_id)
        }
      }
    }
  }

  private previous() {
    this.stepper--
  }

  private isGroupEnabled(groupID: number) {
    return this.enabledService.includes(groupID)
  }

  private get hasSchoolSpecificService() {
    return this.services.some((s) => s.school_specific === true)
  }

  private get hasSchoolSpecificServiceSelected() {
    const schoolSpecficService: number[] = this.services
      .filter((s) => s.school_specific === true)
      .map((s) => s.groupe_ad_id)
    return schoolSpecficService.some((s) => this.enabledService.includes(s))
  }

  private async checkExistingAccount() {
    if (this.tempAccount.identifiant) {
      const phone = this.tempAccount.identifiant.replace(/\s/g, '')
      if (phone !== this.lastCheckedPhoneNumber) {
        this.validPhoneNumber = this.phoneNumberRegExp.test(phone)
        if (this.validPhoneNumber) {
          this.lastCheckedPhoneNumber = phone

          this.validatingPhoneNumber = true
          const phoneErrorField = this.$t('Phone').toString()
          try {
            this.existingAccount = await TempAccountService.checkForExistingUsername(
              this.tempAccount.identifiant,
            )

            if (this.existingAccount.etat_refedu === 'Supprimé') {
              this.accountExists = false
              throw new Error('ExceptionUserWasDeleted')
            }
            this.accountExists = true

            const phoneErrorMessage = this.$t(
              'TEMPACCOUNT.CreateTempAccountPhoneAlreadyTaken',
            ).toString()
            this.$validator.errors.add({
              field: phoneErrorField,
              msg: phoneErrorMessage,
              rule: 'unique',
            })
          } catch (error: any) {
            this.$validator.errors.remove(phoneErrorField)
            this.accountExists = false
            this.existingAccount = {} as TempAccount
          } finally {
            this.validatingPhoneNumber = false
          }
        }
      }
    }
  }

  private async checkExistingMail() {
    const email = this.tempAccount.courriel_autre
    this.validEmail = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(
      this.tempAccount.courriel_autre,
    )
    if (this.validEmail === true) {
      if (email !== this.lastCheckedEmail) {
        this.lastCheckedEmail = email
        this.validatingEmailAddress = true
        const MailErrorField = this.$t('Email').toString()
        try {
          this.emailExists = await TempAccountService.checkForExistingMail(
            this.tempAccount.courriel_autre,
          )
          this.emailExists = true

          const emailErrorMessage = this.$t(
            'TEMPACCOUNT.CreateTempAccountEmailAlreadyTaken',
          ).toString()
          this.$validator.errors.add({
            field: MailErrorField,
            msg: emailErrorMessage,
            rule: 'unique',
          })
        } catch (error: any) {
          this.$validator.errors.remove(MailErrorField)
          this.emailExists = false
        } finally {
          this.validatingEmailAddress = false
        }
      }
    }
  }

  private get existingAccountIcon() {
    const icon = 'mdi-account'
    const append =
      this.validPhoneNumber && !this.isPending
        ? this.accountExists
          ? '-alert'
          : '-check'
        : ''
    return icon + append
  }

  private get existingAccountIconColor() {
    return this.validPhoneNumber && !this.isPending
      ? this.accountExists
        ? 'error'
        : 'success'
      : ''
  }

  private get existingEmailIcon() {
    const icon = 'mdi-email'
    const append =
      this.validEmail && !this.isPending
        ? this.emailExists
          ? '-alert'
          : '-check'
        : ''
    return icon + append
  }

  private get existingEmailIconColor() {
    return this.validEmail && !this.isPending
      ? this.emailExists
        ? 'error'
        : 'success'
      : ''
  }

  // @Watch('service', { deep: true })
  private updateEnabledServices(serviceID: number) {
    if (this.enabledService.includes(serviceID)) {
      this.enabledService.splice(this.enabledService.indexOf(serviceID), 1)
    } else {
      this.enabledService.push(serviceID)
    }
    if (!this.hasSchoolSpecificServiceSelected) {
      this.selectedSchool = null
    }
  }

  private async create() {
    this.isPending = true
    if (this.hasSchoolSpecificServiceSelected && this.selectedSchool === null) {
      this.errorHandler.handle(this, 7, true)
      this.isPending = false
      return
    }
    try {
      this.tempAccount.identifiant = this.tempAccount.identifiant.replace(
        /\s/g,
        '',
      )

      const createdTempAccount = await TempAccountService.createTempAccount({
        account: this.tempAccount,
        services: this.enabledService,
        roles: this.currentUser.roles,
        target: 'Basique',
        school: this.selectedSchool,
      })
      this.hide()
      this.$notificationService.created(
        this.$t('TEMPACCOUNT.CreateSuccess').toString() +
          ' ' +
          this.tempAccount.courriel_autre,
      )
    } catch (error: any) {
      if (error.response !== undefined) {
        this.errorHandler.handle(this, error.response.data.code, true)
      }
    } finally {
      this.isPending = false
    }
  }

  private print(tempAccount: TempAccount) {
    (this.$refs.printDialog as any).show(tempAccount)
  }
}
